import React, { useCallback, useEffect, useState } from 'react';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Toggle } from '@uhg-abyss/web/ui/Toggle';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Router } from '@uhg-abyss/web/ui/Router';
import { API } from '@src/api';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { memberInContextAtom, identifiedNeedsAtom, vendorAtom } from '@src/global-state';
import { useAtom } from 'jotai';
import { NeedType } from '@src/api.types';
import { getDeclinedScreener, saveVendor } from '@src/utills/storage';
import {
  CboSearchParams,
  searchParamsFromUrl,
} from '@src/routes/SearchImproved/SearchImproved';

export const IdentifiedNeeds = () => {
  const { navigate, getSearchParams } = useRouter();
  const { getRouteParams } = useRouter();
  const params = getRouteParams();
  const [memberInContext, setMemberInContext] = useAtom(memberInContextAtom);
  const [identifiedNeeds, setIdentifiedNeeds] = useAtom(identifiedNeedsAtom);
  const [vendorCache, setVendorCache] = useAtom(vendorAtom);
  const memberScreeningid: string | undefined = params.id ?? undefined;
  let getNeedsApiResponse;

  const [needs, setNeeds] = useState<NeedType[]>();
  let category: string = '';
  const [vendor, setVendor] =
    useState<{ id: number; name: string; keyword: string }>();

  const getNeeds = useCallback(() => {}, []);
  useEffect(() => {
    const payload = {
      memberScreeningId: memberScreeningid,
    };

    API.getIdentifiedNeeds(payload).then(
      async (data: any) => {
        getNeedsApiResponse = data?.data;
        getNeedsApiResponse?.forEach((element) => {
          // eslint-disable-next-line no-param-reassign
          element.requestedAssistance = true;
        });
        setNeeds(getNeedsApiResponse);
      },
      (err) => {
      }
    );
  }, [memberScreeningid]);
  getNeeds();

  const getProvider = useCallback(() => {}, []);
  useEffect(() => {
    async function fetchProvider(zipcode) {
      // Use cached vendor if zipcode matches; otherwise fetch and cache.
      let resp = (vendorCache !== null && vendorCache.zipcode === zipcode) ? vendorCache.response : null;
      if (!resp) {
        resp = await API.fetchProviderToUse({
          zipcode,
          lineOfBusiness: memberInContext?.lineOfBusiness,
        });
        if (resp?.status === 'success') {
          setVendorCache({ zipcode, response: resp });
        }
      }
      setVendor(resp?.provider);
      saveVendor(resp?.provider?.name);
    }
    if (memberInContext?.addrZipCode)
      fetchProvider(memberInContext?.addrZipCode);
  }, [memberInContext?.addrZipCode]);

  getProvider();

  const saveIdentifiedNeeds = async (payload) => {
    const response = await API.saveIdentifiedNeeds(payload);
    if (response.status === 'success') return true;

    return false;
  };

  const firstName = memberInContext?.first_name;
  const today = new Date()
    .toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '-');

  function numberToWords(num) {
    const units = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const teens = [
      '',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];
    const tens = [
      '',
      'Ten',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const thousands = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];

    const toWords = (number) => {
      if (number === 0) return 'Zero';
      let words = '';
      for (let i = 0; i < thousands.length && number > 0; i++) {
        if (number % 1000 !== 0) {
          words = `${helper(number % 1000) + thousands[i]} ${words}`;
        }
        number = Math.floor(number / 1000);
      }
      return words.trim();
    };

    const helper = (num) => {
      if (num === 0) return '';
      if (num < 10) return `${units[num]} `;
      if (num < 20) return `${teens[num - 10]} `;
      return `${tens[Math.floor(num / 10)]} ${units[num % 10]} `;
    };

    return toWords(num);
  }

  function formatNeedsText(totalNeeds) {
    if (totalNeeds === 0) {
      return 'No needs have';
    }
    if (totalNeeds === 1) {
      return `One (1) need has`;
    }
    const totalNeedsInWords = numberToWords(totalNeeds);
    return `${totalNeedsInWords} (${totalNeeds}) needs have`;
  }

  const totalNeeds = needs?.length;
  const needsText = formatNeedsText(totalNeeds);

  const text =
    totalNeeds === 0 ? (
      <span>
        Based on the answers {firstName} provided, there are zero (0) identified
        needs based on Social Determinants of Health (SDOH). You may choose to
        retake this assessment or go to our search tool to find community based
        organizations (CBOs) for this member. <br />
        <Divider />
        <br />
        <Button variant="outline" size="$md" href="/question">
          Retake Assessment
        </Button>{' '}
        <Button size="$md" href={`/search?zipcode=${memberInContext?.zipCode}`}>
          Search for a CBO
        </Button>{' '}
      </span>
    ) : (
      `
      ${needsText} been identified from today’s
      assessment. Ask the member if they would like help with the need${
        totalNeeds === 1 ? '' : 's'
      }
      listed below and decline assistance by clicking the checkmark beside
      each need. Once you have indicated whether the member has requested or
      declined assistance for each need, click “Search for a CBO” beside a
      need to begin your search.
  `
    );

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const toggelModal = () => {
    setIsDeclineModalOpen(true);
  };
  const noFinishFunc = () => {
    setIsDeclineModalOpen(false);
  };
  const yesDecline = async () => {
    setIsDeclineModalOpen(false);

    await API.declineAllAssistance(memberScreeningid).then(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      async (data: any) => {
      },
      (err) => {
      }
    );
    navigate(`/search?zipcode=${memberInContext?.addrZipCode}`);
  };

  const handleSearchCBOClick = async (item) => {
    const selectedNeed = item;
    const payloadForSavingNeeds = {
      memberScreeningId: memberScreeningid,
      needs,
    };

    const res = await saveIdentifiedNeeds(payloadForSavingNeeds);

    if (res) {
      const requestedNeeds: NeedType[] = [];
      needs?.forEach((n) => {
        if (n?.requestedAssistance) {
          const s = { needId: n?.needId, need: n?.need };
          requestedNeeds.push(s);
        }
      });

      const payloadForGettingCatForNeeds = {
        providerId: (vendor?.name === 'findhelp'
          ? '1001'
          : '1005') as `${number}`,
        requestedNeedIds: [selectedNeed.needId],
      };

      const response = await API.getCategoryForNeed(
        payloadForGettingCatForNeeds
      );

      const categoryMapping = response?.data?.[0] as {
        categoryId: string;
        category: string;
        needId: string;
      };

      category = categoryMapping?.category ?? '';

      setIdentifiedNeeds(requestedNeeds);

      navigate(
        `/search?category=${encodeURIComponent(
          category
        )}&zipcode=${encodeURIComponent(
          memberInContext?.zipCode ?? ''
        )}&filters=&sortBy=relevance&recordsPerPage=25&page=1`
      );
    } else {
    }
  };

  return (
    <React.Fragment>
      <div
        style={{
          paddingLeft: '100px',
          paddingRight: '100px',
          paddingTop: '2%',
        }}
      >
        <Grid>
          <Grid.Col span={9}>
            <Heading level={1} data-cy="main-heading">
              <Layout.Group>
                <IconBrand icon="heart" size={48} variant="twotonedarkcircle" />
                <span>{firstName}'s Identified Needs</span>
              </Layout.Group>
            </Heading>
          </Grid.Col>
          <Grid.Col span={3}>
            <React.Fragment>
              <Router.MetaTags title="Home" />
              {totalNeeds === 0 ? (
                ''
              ) : (
                <Layout.Group alignLayout="center">
                  <Button
                    id="declineAllAssistance"
                    variant="outline"
                    onClick={toggelModal}
                    aria-haspopup="dialog"
                  >
                    Decline All assistance
                  </Button>
                  <ModalDialog
                    header="Are you sure you want to decline all assistance?"
                    isOpen={isDeclineModalOpen}
                    onClose={noFinishFunc}
                    size="$md"
                    css={{
                      'abyss-modal-header-container': { marginTop: '$sm' },
                    }}
                  >
                    <div>
                      <Text>
                        This action will decline all of the identified needs but
                        will keep a record of today's assessment in the member's
                        profile. Declining assistance will take you to the main
                        search page for finding community based organizations.
                      </Text>
                    </div>
                    <div>
                      <Layout.Group
                        alignLayout="left"
                        style={{ paddingBottom: '19px' }}
                      >
                        <Button
                          variant="outline"
                          style={{ width: '100%' }}
                          onClick={noFinishFunc}
                        >
                          Cancel
                        </Button>

                        <Button
                          href="#"
                          onClick={yesDecline}
                          style={{ width: '100%' }}
                        >
                          Yes, member declines all assistance
                        </Button>
                      </Layout.Group>
                    </div>
                  </ModalDialog>
                </Layout.Group>
              )}
            </React.Fragment>
          </Grid.Col>
        </Grid>
        <br />
        <br />
        <Heading level={4}>Suggested Script:</Heading>
        <br />
        <Text>
          {totalNeeds === 0 ? (
            <div style={{ fontStyle: 'italic' }}>
              "Thank you for sharing with me today. Based on your responses, we
              did not identify any social needs today.
              <br />
              Would you like to retake the assessment or is there something we
              didn’t cover that you would like to share with me? I would be
              happy to still search for resources for you.
              <br />
              <br />
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                <li>
                  <em>
                    If yes: Retake the assessment or click “Search for CBOs” and
                    begin searching for resources for the member.
                  </em>
                </li>
                <li>
                  <em>
                    If the member does not want to search for CBOs, you may say:
                    “If needs arise do not hesitate to reach out for support. We
                    are happy to help.”
                  </em>
                </li>
              </ul>
            </div>
          ) : (
            <em>
              “{firstName}, thank you for sharing with me today. Now, let’s go
              through the needs that we identified so that I can search for
              resources in your community to help”.
            </em>
          )}
        </Text>

        <br />
        <br />
        <br />
        <Heading level={4} data-cy="today-assessment">
          Today's Assessment ({today})
        </Heading>
        <br />
        <Text>{text}</Text>
        <br />
        <br />
        <br />
        <br />
        {totalNeeds === 0 ? (
          ''
        ) : (
          <React.Fragment>
            {needs
              ?.sort((a, b) => {
                return a.need.localeCompare(b.need);
              })
              .map((item, idx) => {
                // const isChecked = true;

                return (
                  <div>
                    <br />
                    <Grid key={idx}>
                      <Grid.Col style={{ paddingTop: '20px' }} span={6}>
                        <Heading level={6}>
                          {idx + 1}. {item.need}
                        </Heading>
                      </Grid.Col>
                      <Grid.Col style={{ paddingTop: '20px' }} span={3}>
                        <Toggle
                          label={item.requestedAssistance ? 'Yes, request assistance' : 'Decline Assistance'}
                          isChecked={item.requestedAssistance}
                          onChange={(e) => {
                            // eslint-disable-next-line no-param-reassign
                            item.requestedAssistance =
                              !item.requestedAssistance;

                            const json: any = needs;
                            json.find((x) => {
                              return x.needId === item.needId;
                            });
                            // .isChecked = item.requestedAssistance;
                            setNeeds(JSON.parse(JSON.stringify(json)));
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <Button
                          isDisabled={!item.requestedAssistance}
                          onClick={(e) => {
                            handleSearchCBOClick(item);
                          }}
                        >
                          Search for a CBO
                        </Button>
                      </Grid.Col>
                      <Divider />
                    </Grid>
                  </div>
                );
              })}
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};
